import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { AddressSearchInfoToZipCodeDto, Sgkd034aResponse, Sgkd034aResponseClmtoAddrInfo } from 'sg-api';
import { SgKdConstants } from '../../../../../sg-kd-shared/sg-kd-constants';
import { UsedSzza001aService } from '../../../../../sg-kd-shared/services/used-szza001a.service';
import { ZipcodeFormatService } from '../../../../../sg-kd-shared/services/zipcode-format.service';
import { takeUntil } from 'rxjs/operators';
import { SelectBox } from 'cbc-shared-lib';

@Component({
  selector: 'app-sgkd019d-sendto-info',
  templateUrl: './sgkd019d-sendto-info.component.html',
  styleUrl: './sgkd019d-sendto-info.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgkd019dSendtoInfoComponent implements OnInit, OnDestroy {

  private onDestroy$ = new Subject();

  /** メインフォーム */
  mainForm: UntypedFormGroup;
  /** 送付先情報フォーム */
  infoForm: UntypedFormGroup;
  /** レスポンス */
  response: Sgkd034aResponse;

  /** メッセージクリアイベント */
  @Output() clearMessageEvent = new EventEmitter();

  /** 郵便番号検索フラグ */
  private postnoSearch: boolean = false;
  /** 都道府県リスト */
  pfcAnameList: SelectBox[];
  /** 行政市区リスト */
  admncwnmList: SelectBox[];
  /** 行政町名リスト */
  admntnmList: SelectBox[];
  /** 丁目リスト */
  chomeList: SelectBox[];

  /** 郵便番号検索結果 */
  private addressList: Array<AddressSearchInfoToZipCodeDto>;

  constructor(
    private parent: FormGroupDirective,
    private formBuilder: UntypedFormBuilder,
    private usedSzza001aService: UsedSzza001aService,
    private zipcodeFormatService: ZipcodeFormatService) {
  }

  /**
   * 初期処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  ngOnInit(): void {
    this.mainForm = this.parent.form;
    this.infoForm = this.createForm();
    this.mainForm.addControl('infoForm', this.infoForm);
  }

  /**
   * 破棄処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    if (this.mainForm) {
      this.mainForm.clearValidators();
      this.mainForm.updateValueAndValidity();
      this.mainForm.removeControl('infoForm');
    }
  }

  /**
   * クリアボタン押下処理
   */
  onClickClear(): void {
    this.clearControlForm();
  }

  /**
   * 郵便番号フォーカスアウト
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onBlurZipcode(): void {
    let targetZipcode = this.infoForm.get('postNo').value;
    let hyphenZipcode = this.zipcodeFormatService.formatZipcode(targetZipcode);
    this.infoForm.get('postNo').setValue(hyphenZipcode);
  }

  /**
   * 郵便番号フォーカスイン
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onFocusZipcode(): void {
    let targetZipcode = this.infoForm.get('postNo').value;
    let noHyphenZipcode = this.zipcodeFormatService.reformatZipcode(targetZipcode);
    this.infoForm.get('postNo').setValue(noHyphenZipcode);
  }

  /**
   * チェックボックス変更イベント
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onCheckboxChange(): void {
    // 住所項目のクリア
    this.clearAddress();
    if (this.infoForm.get('cbClmto').value) {
      this.setAddress(this.response.responseDto.clmtoAddrInfo);
      this.onBlurZipcode();
      this.infoForm.get('cbInput').setValue(false);
      this.disableAddress();
      this.postnoSearch = false;
      return;
    }
    if (this.infoForm.get('cbCtrper').value) {
      this.setAddress(this.response.responseDto.ctrperAddrInfo);
      this.onBlurZipcode();
      this.infoForm.get('cbInput').setValue(false);
      this.disableAddress();
      this.postnoSearch = false;
      return;
    }
    if (this.infoForm.get('cbSupplydestination').value) {
      this.setAddress(this.response.responseDto.supplydestinationAddrInfo);
      this.onBlurZipcode();
      this.infoForm.get('cbInput').setValue(false);
      this.disableAddress();
      this.postnoSearch = false;
      return;
    }
  }

  /**
   * 手入力チェックボックス変更イベント
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onCheckboxInputChange(): void {
    if (this.infoForm.get('cbInput').value) {
      if (this.infoForm.get('cbClmto').enabled) {
        this.infoForm.get('cbClmto').setValue(false);
      }
      if (this.infoForm.get('cbCtrper').enabled) {
        this.infoForm.get('cbCtrper').setValue(false);
      }
      if (this.infoForm.get('cbSupplydestination').enabled) {
        this.infoForm.get('cbSupplydestination').setValue(false);
      }
      // 住所項目の活性化
      this.enableAddress();
      return;
    } else {
      this.disableAddress();
      this.onCheckboxChange();
      this.postnoSearch = false;
    }
  }

  /**
   * 郵便番号検索ボタン押下処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  clickCZipCodeSearch(): void {

    // 画面上で以下条件はボタンが押下できないため削除
    // if (this.infoForm.get('postNo').value === null || !this.infoForm.get('postNo')?.valid) {
    //   return;
    // }

    this.clearMessageEvent.emit();
    this.addressList = [];

    let targetZipcode = this.infoForm.get('postNo').value;
    let noHyphenZipcode = this.zipcodeFormatService.reformatZipcode(targetZipcode);
    this.usedSzza001aService.doSzza012a(noHyphenZipcode)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        response => {
          // 住所選択セレクトボックスのクリア
          this.clearAddressSelectBox();
          // 住所選択セレクトボックスの生成
          this.createAddressSelectBox(response.addressSearchInfoToZipCodeDtoList);
          // 番地・号
          this.infoForm.get('buildingnumber').setValue(SgKdConstants.STR.EMPTY);
          // 住宅・号棟漢字名
          this.infoForm.get('visitSupadrAname').setValue(SgKdConstants.STR.EMPTY);
          // 屋号・業種漢字名
          this.infoForm.get('visitYagouAname').setValue(SgKdConstants.STR.EMPTY);
          this.postnoSearch = true;
        }, error => {
        }
      );
  }

  /**
   * 住所情報・お客さま氏名取得APIレスポンス設定処理
   * 
   * @param response 住所情報・お客さま氏名取得APIレスポンス
   * @memberof Sgkd019dSendtoInfoComponent
   */
  setResponse(response: Sgkd034aResponse) {
    this.response = response;
    // 請求先住所
    if (this.response.responseDto.clmtoAddrInfo.postno === null ||
      typeof this.response.responseDto.clmtoAddrInfo.postno === 'undefined') {
      this.infoForm.controls.cbClmto.disable();
    } else {
      this.infoForm.controls.cbClmto.enable();
      this.infoForm.controls.cbClmto.setValue(true);
    }
    // 契約者住所
    if (this.response.responseDto.ctrperAddrInfo.postno === null ||
      typeof this.response.responseDto.ctrperAddrInfo.postno === 'undefined') {
      this.infoForm.controls.cbCtrper.disable();
    } else {
      this.infoForm.controls.cbCtrper.enable();
      this.infoForm.controls.cbCtrper.setValue(true);
    }
    // 供給先住所
    if (this.response.responseDto.supplydestinationAddrInfo.postno === null ||
      typeof this.response.responseDto.supplydestinationAddrInfo.postno === 'undefined') {
      this.infoForm.controls.cbSupplydestination.disable();
    } else {
      this.infoForm.controls.cbSupplydestination.enable();
      this.infoForm.controls.cbSupplydestination.setValue(true);
    }
    // 手入力
    this.infoForm.controls.cbInput.enable();

    this.onCheckboxChange();
  }

  /**
   * 住所検索判定
   * 
   * @returns 住所検索の場合true
   * @memberof Sgkd019dSendtoInfoComponent
   */
  isPostnoSearch(): boolean {
    return this.postnoSearch;
  }

  /**
   * Formの生成処理
   * 
   * @returns 本コンポーネントのForm情報
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private createForm(): UntypedFormGroup {
    let form = this.formBuilder.group({
      /** 郵便番号 */
      postNo: new UntypedFormControl({ value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.POST_NO),
          Validators.pattern(SgKdConstants.PATTERN.POST_NO)
        ]),
      /** 請求先住所 */
      cbClmto: new UntypedFormControl({ value: false, disabled: true }),
      /** 契約者住所 */
      cbCtrper: new UntypedFormControl({ value: false, disabled: true }),
      /** 供給先住所 */
      cbSupplydestination: new UntypedFormControl({ value: false, disabled: true }),
      /** 手入力 */
      cbInput: new UntypedFormControl({ value: false, disabled: true }),
      /** 都道府県（手入力） */
      pfcAnameInput: new UntypedFormControl({ value: '', disabled: false },
        [ Validators.required ]),
      /** 行政市区（手入力） */
      admncwnmInput: new UntypedFormControl({ value: '', disabled: false },
        [ Validators.required ]),
      /** 行政町名（手入力） */
      admntnmInput: new UntypedFormControl({ value: '', disabled: false },
        [ Validators.required ]),
      /** 丁目（手入力） */
      chomeInput: new UntypedFormControl({ value: '', disabled: false }),
      /** 都道府県 */
      pfcAname: new UntypedFormControl({ value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.PFC_ANAME)
        ]),
      /** 行政市区 */
      admncwnm: new UntypedFormControl({ value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.ADMNCWNM)
        ]),
      /** 行政町名 */
      admntnm: new UntypedFormControl({ value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.ADMNTNM)
        ]),
      /** 丁目 */
      chome: new UntypedFormControl({ value: '', disabled: true },
        [ Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.CHOME) ]),
      /** 番地・号 */
      buildingnumber: new UntypedFormControl({ value: '', disabled: true },
        [ Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.BUILDING_NUMBER) ]),
      /** 住宅・号棟漢字名 */
      visitSupadrAname: new UntypedFormControl({ value: '', disabled: true },
        [ Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.VISIT_SUPADR) ]),
      /** 屋号・業種漢字名 */
      visitYagouAname: new UntypedFormControl({ value: '', disabled: true },
        [ Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.VISIT_YAGOU) ]),
      /** 送付先宛名 */
      sendtoAddress: new UntypedFormControl({ value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.SENDTO_ADDRESS)
        ]),
    });
    return form;
  }

  /**
   * Formのクリア処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private clearControlForm(): void {
    // チェックボックスのクリア
    this.infoForm.get('cbClmto').setValue(false);
    this.infoForm.get('cbCtrper').setValue(false);
    this.infoForm.get('cbSupplydestination').setValue(false);
    this.infoForm.get('cbInput').setValue(false);
    // 住所項目のクリア
    this.clearAddress();
    this.disableAddress();
    // 送付先宛名のクリア
    this.infoForm.get('sendtoAddress').setValue(SgKdConstants.STR.EMPTY);
  }

/**
   * 住所項目のクリア処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private clearAddress(): void {
    this.infoForm.get('postNo').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('pfcAname').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('admncwnm').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('admntnm').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('chome').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('pfcAnameInput').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('admncwnmInput').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('admntnmInput').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('chomeInput').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('buildingnumber').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('visitSupadrAname').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('visitYagouAname').setValue(SgKdConstants.STR.EMPTY);
    this.infoForm.get('sendtoAddress').setValue(SgKdConstants.STR.EMPTY);
  }

  /**
   * 住所項目の設定処理
   * 
   * @param info 住所情報
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private setAddress(info: Sgkd034aResponseClmtoAddrInfo): void {
    this.infoForm.get('postNo').setValue(info.postno ?? "");
    this.infoForm.get('pfcAname').setValue(info.prefectureaname ?? "");
    this.infoForm.get('admncwnm').setValue(info.administrativecitywardname ?? "");
    this.infoForm.get('admntnm').setValue(info.choname ?? "");
    this.infoForm.get('chome').setValue(info.chome ?? "");
    this.infoForm.get('buildingnumber').setValue(info.buildingnumber ?? "");
    this.infoForm.get('visitSupadrAname').setValue(info.visitSupadrAname ?? "");
    this.infoForm.get('visitYagouAname').setValue(info.visitYagouAname ?? "");
    this.infoForm.get('sendtoAddress').setValue(info.visitName ?? "");
  }

  /**
   * 住所項目の活性化処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private enableAddress(): void {
    this.infoForm.get('postNo').enable();
    this.infoForm.get('pfcAname').enable();
    this.infoForm.get('admncwnm').enable();
    this.infoForm.get('admntnm').enable();
    this.infoForm.get('chome').enable();
    this.infoForm.get('pfcAnameInput').enable();
    this.infoForm.get('admncwnmInput').enable();
    this.infoForm.get('admntnmInput').enable();
    this.infoForm.get('chomeInput').enable();
    this.infoForm.get('buildingnumber').enable();
    this.infoForm.get('visitSupadrAname').enable();
    this.infoForm.get('visitYagouAname').enable();
    this.infoForm.get('sendtoAddress').enable();
  }

  /**
   * 住所項目の非活性化処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private disableAddress(): void {
    this.infoForm.get('postNo').disable();
    this.infoForm.get('pfcAname').disable();
    this.infoForm.get('admncwnm').disable();
    this.infoForm.get('admntnm').disable();
    this.infoForm.get('chome').disable();
    this.infoForm.get('pfcAnameInput').disable();
    this.infoForm.get('admncwnmInput').disable();
    this.infoForm.get('admntnmInput').disable();
    this.infoForm.get('chomeInput').disable();
    this.infoForm.get('buildingnumber').disable();
    this.infoForm.get('visitSupadrAname').disable();
    this.infoForm.get('visitYagouAname').disable();
    this.infoForm.get('sendtoAddress').disable();
  }

  /**
   * 住所選択セレクトボックスのクリア
   * 
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private clearAddressSelectBox(): void {
    this.pfcAnameList = [];
    this.admncwnmList = [];
    this.admntnmList = [];
    this.chomeList = [];
  }

  /**
   * 住所選択セレクトボックスの生成
   * 
   * @param addressList 住所情報
   * @memberof Sgkd019dSendtoInfoComponent
   */
  private createAddressSelectBox(addressList: Array<AddressSearchInfoToZipCodeDto>): void {
    
    let wkPfcAnameList = [];
    let wkAdmncwnmList = [];
    let wkAdmntnmList = [];
    let wkChomeList = [];
    addressList.forEach(
      addressInfo => {
        if (!wkPfcAnameList.find((sb) => sb.value === addressInfo.prefecturesName)) {
          wkPfcAnameList.push({ value: addressInfo.prefecturesName, viewValue: addressInfo.prefecturesName});
        }
        if (!wkAdmncwnmList.find((sb) => sb.value === addressInfo.cityName)) {
          wkAdmncwnmList.push({ value: addressInfo.cityName, viewValue: addressInfo.cityName});
        }
        if (!wkAdmntnmList.find((sb) => sb.value === addressInfo.townAreaName)) {
          wkAdmntnmList.push({ value: addressInfo.townAreaName, viewValue: addressInfo.townAreaName});
        }
        if (!wkChomeList.find((sb) => sb.value === addressInfo.ooazaName)) {
          wkChomeList.push({ value: addressInfo.ooazaName, viewValue: addressInfo.ooazaName});
        }
      }
    );
    // 都道府県
    if (wkPfcAnameList.length === 1) {
      this.infoForm.get('pfcAnameInput').setValue(wkPfcAnameList[0].value);
    } else {
      this.infoForm.get('pfcAnameInput').setValue(SgKdConstants.STR.EMPTY);
    }
    // 行政市区
    if (wkAdmncwnmList.length === 1) {
      this.infoForm.get('admncwnmInput').setValue(wkAdmncwnmList[0].value);
    } else {
      this.infoForm.get('admncwnmInput').setValue(SgKdConstants.STR.EMPTY);
    }
    // 行政町名
    if (wkAdmntnmList.length === 1) {
      this.infoForm.get('admntnmInput').setValue(wkAdmntnmList[0].value);
    } else {
      this.infoForm.get('admntnmInput').setValue(SgKdConstants.STR.EMPTY);
    }
    // 丁目
    if (wkChomeList.length === 1) {
      this.infoForm.get('chomeInput').setValue(wkChomeList[0].value);
    } else {
      this.infoForm.get('chomeInput').setValue(SgKdConstants.STR.EMPTY);
    }
    this.pfcAnameList = wkPfcAnameList;
    this.admncwnmList = wkAdmncwnmList;
    this.admntnmList = wkAdmntnmList;
    this.chomeList = wkChomeList;
    this.addressList = addressList;
  }
}
